import React from 'react';
import {
  useRpcMediaAnyCoarsePointer,
  useRpcMediaSmallScreen,
  useRpcMediaMediumScreen,
  useRpcMediaLargeScreen,
  type useMatchMediaReturnType,
} from '../components/hooks/use-match-media/useRpcMatchMedia';

export type UiMediaQueryContextValue = Readonly<{
  /**
   * True if the screen is smaller than the small viewport.
   *
   * If you are using this to set CSS properties, you probably want to use media queries instead: `media.down($['viewport.small'])`
   */
  isSmallScreen?: useMatchMediaReturnType;
  /**
   * True if the screen is smaller than the medium viewport.
   *
   * If you are using this to set CSS properties, you probably want to use media queries instead: `media.down($['viewport.medium'])`
   */
  isMediumScreen?: useMatchMediaReturnType;
  /**
   * True if the screen is smaller than the large viewport.
   *
   * If you are using this to set CSS properties, you probably want to use media queries instead: `media.down($['viewport.large'])`
   */
  isLargeScreen?: useMatchMediaReturnType;
  isTouch?: useMatchMediaReturnType;
}>;

const UiMediaQueryContext = React.createContext<UiMediaQueryContextValue>({});

export const useMediaQuery = (): UiMediaQueryContextValue => {
  return React.useContext(UiMediaQueryContext);
};

export const UiMediaQueryProvider: React.FC<
  React.PropsWithChildren<unknown>
> = ({children}) => {
  /**
   * We initialize these hooks in in a top-level provider instead of in each specific component
   * so that their value is computed as soon as possible when the applications is rendered.
   * Otherwise, animated components such as Dialog or Drawer wouldn't be animated
   * properly on the first render.
   */
  const isSmallScreen = useRpcMediaSmallScreen();
  const isMediumScreen = useRpcMediaMediumScreen();
  const isLargeScreen = useRpcMediaLargeScreen();
  const isTouch = useRpcMediaAnyCoarsePointer();

  const context = React.useMemo(
    () => ({
      isSmallScreen,
      isMediumScreen,
      isLargeScreen,
      isTouch,
    }),
    [isSmallScreen, isMediumScreen, isLargeScreen, isTouch],
  );

  return (
    <UiMediaQueryContext.Provider value={context}>
      {children}
    </UiMediaQueryContext.Provider>
  );
};
